/**
 * DateRangeField — single date-range selector for the campaign form's
 * "Schedule & status" section (Chelsie Aug-14: "Date Range Selection" — one
 * calendar instead of two separate Start/End date inputs).
 *
 * Controlled component: `from`/`to` are the existing yyyy-MM-dd form-state
 * strings (both independently optional — start-only / end-only / none remain
 * representable); every calendar interaction propagates immediately through
 * `onChange(from, to)`. The calendar itself is react-day-picker in range mode
 * with `resetOnSelect`: the first click sets the start (from-only), the second
 * completes the range in whichever direction it falls (an earlier second click
 * shifts the start back), and the next click after a complete range starts a
 * new one — every branch emits an ordered pair, so end >= start holds by
 * construction.
 *
 * The popover is deliberately minimal and hand-rolled — same overlay approach
 * as the rest of this SPA (no radix, no general popover primitive) — but the
 * panel itself renders through a React portal into `document.body`,
 * positioned `fixed` from the trigger's `getBoundingClientRect()` (re-measured
 * on open, window resize and capture-phase scroll; opens upward when there's
 * no room below — task 064-05, D2). Outside-pointerdown detection treats the
 * trigger root *and* the portalled panel as "inside" (D3), since the panel is
 * no longer a DOM descendant of the root once portalled.
 */

import * as React from 'react';
import { format } from 'date-fns';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import { cn } from '../lib/utils';

interface DateRangeFieldProps {
  /** Inclusive start, yyyy-MM-dd ('' = open). */
  from: string;
  /** Inclusive end, yyyy-MM-dd ('' = open). */
  to: string;
  /** Propagates (from, to) after each calendar interaction; '' empties a side. */
  onChange: (from: string, to: string) => void;
}

// yyyy-MM-dd ↔ Date via LOCAL midnight. Never `new Date('yyyy-MM-dd')` here —
// that parses as UTC midnight and shifts the day in non-UTC environments. The
// UTC helpers in campaign-form.tsx own the wire format; this is display only.
const parseLocalDate = (s: string): Date | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const formatLocalDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatDisplayDate = (date: Date): string => format(date, 'MMM d, yyyy');

type PanelPosition = { left: number } & ({ top: number } | { bottom: number });

export function DateRangeField({ from, to, onChange }: DateRangeFieldProps) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<PanelPosition>({ left: 0, top: 0 });
  // The in-progress calendar selection; re-synced from the form state each
  // time the popover opens. No other durable state of its own.
  const [draft, setDraft] = React.useState({ from, to });

  // Re-measure the trigger (and, once mounted, the panel's own height) to
  // decide the fixed top/bottom anchor — downward by default, upward when
  // there isn't room below.
  const measure = React.useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < panelHeight && spaceAbove > spaceBelow;
    setPosition(
      openUpward
        ? { left: rect.left, bottom: window.innerHeight - rect.top + 4 }
        : { left: rect.left, top: rect.bottom + 4 }
    );
  }, []);

  React.useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  React.useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', measure);
    document.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      document.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  // Close on outside pointerdown and Escape while open — "inside" means the
  // trigger root or the portalled panel.
  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node)) return;
      const insideRoot = rootRef.current?.contains(e.target) ?? false;
      const insidePanel = panelRef.current?.contains(e.target) ?? false;
      if (!insideRoot && !insidePanel) setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const openPopover = () => {
    setDraft({ from, to });
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPosition({ left: rect.left, top: rect.bottom + 4 });
    setOpen(true);
  };

  const fromDate = parseLocalDate(from);
  const toDate = parseLocalDate(to);

  // "MMM D, YYYY – MMM D, YYYY", open-ended for partial values, placeholder
  // when both sides are empty.
  const display =
    fromDate && toDate
      ? `${formatDisplayDate(fromDate)} – ${formatDisplayDate(toDate)}`
      : fromDate
        ? `${formatDisplayDate(fromDate)} – …`
        : toDate
          ? `… – ${formatDisplayDate(toDate)}`
          : 'Select dates';
  const isPlaceholder = !fromDate && !toDate;

  const selected: DateRange | undefined =
    draft.from || draft.to
      ? { from: parseLocalDate(draft.from), to: parseLocalDate(draft.to) }
      : undefined;

  const handleSelect = (range: DateRange | undefined) => {
    const next = {
      from: range?.from ? formatLocalDate(range.from) : '',
      to: range?.to ? formatLocalDate(range.to) : '',
    };
    setDraft(next);
    onChange(next.from, next.to);
    if (next.from && next.to) setOpen(false); // complete range picked → dismiss
  };

  const handleClear = () => {
    setDraft({ from: '', to: '' });
    onChange('', '');
  };

  const draftFrom = parseLocalDate(draft.from);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Start and end date"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openPopover())}
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-lg bg-surface px-3 py-2 text-left text-sm outline-none transition',
          'hover:bg-line/40 focus:shadow-[inset_0_0_0_2px_var(--color-accent)]',
          isPlaceholder ? 'text-faint' : 'text-ink'
        )}
      >
        <span className="min-w-0 flex-1 truncate">{display}</span>
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          className={cn('shrink-0 transition-transform', open ? 'rotate-180' : '')}
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: position.left,
              ...('top' in position ? { top: position.top } : { bottom: position.bottom }),
            }}
            className="z-[60] rounded-lg bg-surface p-2 text-ink hairline shadow-lg"
            // The Field label wrapping this control (when used inside one) is
            // no longer a DOM ancestor of this portalled panel, so it can no
            // longer forward a click on a non-interactive panel area (weekday
            // headers, gutters) to the trigger — but this guard is cheap
            // insurance and the day/Clear buttons keep their own handlers.
            onClick={(e) => e.preventDefault()}
          >
            <DayPicker
              mode="range"
              numberOfMonths={2}
              resetOnSelect
              selected={selected}
              onSelect={handleSelect}
              defaultMonth={
                draftFrom ? new Date(draftFrom.getFullYear(), draftFrom.getMonth(), 1) : undefined
              }
            />
            <div className="mt-1 flex justify-end border-t border-line pt-1">
              <button
                type="button"
                onClick={handleClear}
                className="rounded-md px-2 py-1 text-xs font-semibold text-accent transition hover:bg-paper hover:text-ink"
              >
                Clear
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
